import {
  Box,
  Card,
  CardContent,
  Link,
  Typography,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import CircularDeterminate from '@components/CircularProgress/CircularProgress';
import { DialogListener } from '@components/Dialog';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import HealthScoreInfoModalContent from '@components/Modal/Content/HealthScoreInfoModalContent';

import {
  HEALTH_SCORE_CONTRIBUTOR_MAP,
  HealthScoreType,
} from '@constants/health-score-info';
import { HEALTH_SCORE } from '@constants/routes';

import { formatDate } from '@utils/format';
import { getHealthScoreColor } from '@utils/health-score-color';

import { type HealthMetric } from './HealthScores';

const messages = defineMessages({
  howCalculated: {
    id: 'healthScores.info.subtitle',
    defaultMessage: 'How was this calculated?',
  },
});

interface HealthScoreCardProps {
  metric: HealthMetric;
}
export const getScoreColor = (
  id: string,
  score: number | null,
  theme: any
): string => {
  if (score === null) return theme.palette.grey[300];

  const config = HEALTH_SCORE_CONTRIBUTOR_MAP()[id];
  if (config.thresholds.length === 0) return theme.palette.purple.main;

  // Compute effective score (invert if needed)
  const effectiveScore =
    id === HealthScoreType.Immune
      ? 100 - (score / 5) * 100
      : id === HealthScoreType.Stress
        ? 100 - score
        : score;

  // Find matching threshold
  const matched = config.thresholds.find((t) => {
    const lowerCheck =
      (t.lowerInclusive ?? true)
        ? effectiveScore >= t.lower
        : effectiveScore > t.lower;
    const upperCheck =
      (t.upperInclusive ?? true)
        ? effectiveScore <= t.upper
        : effectiveScore < t.upper;
    return lowerCheck && upperCheck;
  });

  return matched ? theme.palette[matched.color].main : theme.palette.grey[300];
};

const HealthScoreCard: React.FC<HealthScoreCardProps> = ({ metric }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isEmpty = metric.score === null;

  const config = HEALTH_SCORE_CONTRIBUTOR_MAP()[metric.id];
  const color = getHealthScoreColor({
    id: metric.id,
    score: metric.score,
    theme,
  });

  return (
    <Link
      onClick={() => {
        navigate(`${HEALTH_SCORE}${metric.id}`);
      }}
      sx={{ textDecoration: 'none' }}
    >
      <Card
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: 2,
          minHeight: 200,
          maxHeight: 230,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            color: theme.palette.primary.main,
          }}
        >
          <Icon color="primary.main" name={config.icon} size="lg" type="fas" />
        </Box>

        <DialogListener
          persistent
          title={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: 25,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: theme.palette.primary.main,
              }}
            >
              {formatMessage(metric.name)}
            </Typography>
          }
          content={
            <HealthScoreInfoModalContent
              metricId={metric.id}
              isEmpty={isEmpty}
            />
          }
        >
          {({ handleOpen }) => (
            <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
              <Tooltip title={formatMessage(messages.howCalculated)}>
                <span>
                  <IconButton
                    name="circle-info"
                    iconType="far"
                    onClick={handleOpen}
                    color={theme.palette.primary.main}
                    size={20}
                  />
                </span>
              </Tooltip>
            </Box>
          )}
        </DialogListener>

        <CardContent sx={{ textAlign: 'center', mt: 4, width: '100%' }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularDeterminate
              value={
                isEmpty
                  ? 100
                  : metric.id === HealthScoreType.Immune
                    ? (metric.score! / 5) * 100
                    : metric.score!
              }
              progressColor={color}
            />

            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1.1,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 650, color, mb: 0.3 }}>
                {isEmpty ? '--' : metric.score}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: '0.7rem',
                }}
              >
                {metric.id === HealthScoreType.Immune ? '/5' : '/100'}
              </Typography>
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
            {formatMessage(metric.name)}
          </Typography>
          {isEmpty ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 0.1,
                lineHeight: 1.4,
                fontSize: 14,
              }}
            >
              {'--'}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {formatDate({ date: metric.date, formatter: 'date' })}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default HealthScoreCard;
